import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardHeader,
    CardContent,
    CardActions,
    Grid,
    Paper,
    TextField,
    Typography,
    InputAdornment,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArchiveRoundedIcon from "@mui/icons-material/ArchiveRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import { useAuth, useLoading, useSnackbar, useNavigation } from "../../Context/Contexts";
import { SystemLogArchiveViewModel } from "../../Types/ExportTypes";
import CenteredListCard from "../../components/List/CenteredListCardLayout";
import useCachedListQuery from "../../hooks/useCachedListQuery";
import { useTranslation } from "react-i18next";
import ResponsivePagination from "../../components/ResponsivePagination";

const SystemLogArchivePage: React.FC = () => {
    const { authService } = useAuth();
    const { showLoading, hideLoading, isLoading } = useLoading();
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const { navigate } = useNavigation();
    const [items, setItems] = useState<SystemLogArchiveViewModel[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [query, setQuery] = useCachedListQuery({ page: 0, pageSize: 10, filter: "", filters: [] });
    const [filter, setFilter] = useState("");

    const fetchLogs = async () => {
        showLoading();
        try {
            const res = await authService.post<{ items: SystemLogArchiveViewModel[]; count: number }>(
                "/systemLogApi/GetArchivedLogs",
                query
            );
            setItems(res.items);
            setTotalCount(res.count);
        } catch (error: any) {
            showSnackbar(`${t("common.failedToFetchData")}: ${error.response?.data ?? error.message}`, "error");
        } finally {
            hideLoading();
        }
    };

    useEffect(() => { fetchLogs(); }, [query]);

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setQuery({ ...query, page: 0, filter });
    };

    const handlePageChange = (newPage: number) => {
        setQuery({ ...query, page: newPage });
    };

    const openArchiveInNewTab = async (id: number) => {
        showLoading();
        try {
            const blob = await authService.get<Blob>(`/systemLogApi/GetArchiveFile?id=${id}`, "blob");
            const url = URL.createObjectURL(blob);
            const w = window.open(url, "_blank", "noopener,noreferrer");
            if (!w) {
                const a = document.createElement("a");
                a.href = url;
                a.target = "_blank";
                a.rel = "noopener";
                document.body.appendChild(a);
                a.click();
                a.remove();
            }
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (e: any) {
            showSnackbar(`${t("common.openFailed") ?? "Open failed"}: ${e.response?.data ?? e.message}`, "error");
        } finally {
            hideLoading();
        }
    };

    return (
        <CenteredListCard title={t("common.archive")}>
            <Paper sx={{ p: 2 }}>
                <Box
                    component="form"
                    onSubmit={handleSearchSubmit}
                    sx={{ display: "flex", gap: 2, mb: 2 }}
                >
                    <TextField
                        label={t("common.search")}
                        variant="outlined"
                        fullWidth
                        onChange={(e) => setFilter(e.target.value)}
                        value={filter}
                        disabled={isLoading}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchRoundedIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button variant="contained" color="primary" type="submit" disabled={isLoading}>
                        {t("common.search")}
                    </Button>
               
                </Box>
                <Button variant="contained" color="primary" type="button" fullWidth sx={{ mb: 1 }} onClick={() => { navigate({ path: "/admin/systemlog/list" }) }}>
                    {t("common.back")}
                </Button>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {items.map((item) => (
                        <Grid item xs={12} key={item.id}>
                            <Card
                                variant="outlined"
                                sx={(theme) => ({
                                    backgroundColor: alpha(theme.palette.info.light, 0.12),
                                    borderLeft: `6px solid ${theme.palette.info.main}`,
                                })}
                            >
                                <CardHeader
                                    avatar={
                                        <Box
                                            sx={(theme) => ({
                                                width: 40,
                                                height: 40,
                                                borderRadius: "10px",
                                                display: "grid",
                                                placeItems: "center",
                                                background: alpha(theme.palette.info.main, 0.15),
                                            })}
                                        >
                                            <ArchiveRoundedIcon color="info" fontSize="small" />
                                        </Box>
                                    }
                                    titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }}
                                    title={item.fileName || t("common.archive")}
                                    subheader={item.created ? new Date(item.created).toLocaleString() : undefined}
                                />

                                <CardContent>
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: "140px 1fr",
                                            rowGap: 0.5,
                                            columnGap: 2,
                                        }}
                                    >
                                        <Typography variant="body2" color="text.secondary">
                                            ID:
                                        </Typography>
                                        <Typography variant="body2">{item.id}</Typography>

                                        <Typography variant="body2" color="text.secondary">
                                            {t("common.date")}:
                                        </Typography>
                                        <Typography variant="body2">
                                            {item.created ? new Date(item.created).toLocaleString() : "�"}
                                        </Typography>
                                    </Box>
                                </CardContent>

                                <CardActions sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}>
                                    <Button
                                        component="a"
                                        href="#"
                                        onClick={(e) => { e.preventDefault(); openArchiveInNewTab(item.id); }}
                                        size="small"
                                        variant="contained"
                                        color="info"
                                        startIcon={<InsertDriveFileRoundedIcon />}
                                        endIcon={<DownloadRoundedIcon />}
                                    >
                                        {item.fileName ?? t("common.download")}
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}

                    {!isLoading && items.length === 0 && (
                        <Grid item xs={12}>
                            <Box sx={{ py: 6 }}>
                                <Typography align="center">{t("common.noEntriesFound")}</Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>

                <ResponsivePagination


                    page={query.page}


                    pageSize={query.pageSize}


                    totalCount={totalCount}


                    onPageChange={handlePageChange}


                />
            </Paper>
        </CenteredListCard>
    );
};

export default SystemLogArchivePage;